import { Link } from "react-router-dom";
import { api } from "../lib/api";
import type { EntitySummary, TestimonialDTO } from "../../shared/api";
import { EntityCard } from "../components/EntityCard";
import { Spinner } from "../components/States";
import { Testimonials } from "../components/Testimonials";
import { metaFor } from "../../shared/seo";
import { useMeta } from "../lib/seo";
import { useRouteData, HOME_KEY, TESTIMONIALS_KEY } from "../lib/routeData";
import { origin } from "../lib/origin";
import { COMPANY_DIMENSIONS, TOKEN_DIMENSIONS } from "../../shared/dimensions";

// Messaging hierarchy, deliberately in this order:
//   benefit    — know whether you can trust a crypto product
//   proof      — reviews backed by actual usage
//   mechanism  — cryptographic verification
// The mechanism is the differentiator but it is not what a visitor wants in
// the first sentence, so it appears further down rather than in the headline.

export function Home() {
  const { data } = useRouteData<{ items: EntitySummary[] }>(HOME_KEY, () =>
    api.directory({ sort: "reviews", limit: "6" }),
  );
  const top = data?.items ?? null;
  // Until real reviews exist, calling this list "most reviewed" would be a
  // claim the page cannot support — on a site whose whole argument is that its
  // claims are checkable.
  const anyReviews = (top ?? []).some((e) => e.reviewCount > 0);

  const { data: quotes } = useRouteData<{ items: TestimonialDTO[] }>(TESTIMONIALS_KEY, () =>
    api.testimonials(),
  );

  useMeta(metaFor({ route: "home", base: origin() }));

  return (
    <div>
      <section className="hero">
        <span className="badge badge-verified hero-eyebrow">Reviews backed by proof of usage</span>
        <h1 className="hero-title">Know before you trust.</h1>
        <p className="hero-sub">
          Before you trust a crypto product with your money, see what real users experienced
          and whether they can prove they used it.
        </p>
        <div className="row hero-cta">
          <Link to="/companies" className="btn btn-primary">Browse exchanges &amp; wallets</Link>
          <Link to="/tokens" className="btn btn-ghost">Browse tokens</Link>
        </div>
      </section>

      {/* The core story, stated as plainly as it can be. */}
      <section className="section claim-band">
        <p className="claim-a">Anyone can write a review.</p>
        <p className="claim-b">Not everyone can prove they used the product.</p>
        <p className="claim-note">
          Every review here says which it is, so you can weigh them differently.{" "}
          <Link to="/methodology">See how verification works →</Link>
        </p>
      </section>

      <section className="section">
        <div className="row between">
          <h2>{anyReviews ? "Most reviewed" : "In the directory"}</h2>
          <Link to="/companies" className="muted view-all">View all →</Link>
        </div>
        {!anyReviews && (
          <p className="muted section-note">
            Nothing here has been reviewed yet. If you have used one of these, you can be the
            first — and prove it.
          </p>
        )}
        {!top ? (
          <Spinner />
        ) : (
          <div className="grid card-grid">
            {top.map((e) => <EntityCard key={e.id} e={e} />)}
          </div>
        )}
      </section>

      <section className="section">
        <h2>Why CryptoChit</h2>
        <div className="grid why-grid">
          <div>
            <strong>Proof of usage</strong>
            <p>
              A reviewer can prove they held the token or used the exchange. Reviews that carry
              that proof are labelled; the rest are labelled too.
            </p>
          </div>
          <div>
            <strong>You can see who is verified</strong>
            <p>
              Ratings are split into verified and unverified, so a proven customer&rsquo;s
              experience never hides behind an anonymous crowd.
            </p>
          </div>
          <div>
            <strong>Companies cannot buy a rating</strong>
            <p>
              Paid plans buy tools and clearly-labelled placement. They cannot move a score, a
              ranking, or remove a review.
            </p>
          </div>
          <div>
            <strong>Facts, not just opinions</strong>
            <p>
              Contract addresses, automated safety scans and market data come from public
              sources. Nobody can edit them into something flattering.
            </p>
          </div>
        </div>
      </section>

      <section className="section how-band">
        <h2>How it works</h2>
        <ol className="how-steps">
          <li><b>Discover</b><span>Find exchanges, wallets, protocols and tokens.</span></li>
          <li><b>Check</b><span>Read what people experienced, and the objective facts.</span></li>
          <li><b>Verify</b><span>See which reviews are backed by proof of usage.</span></li>
          <li><b>Decide</b><span>Choose with evidence rather than marketing.</span></li>
        </ol>
      </section>

      {/* Real reviews, or an explanation of the mechanism. Never an invented
          reviewer: the quotes here are records a reader can click through and
          check, and a fabricated one would discredit the page it sits on. */}
      <Testimonials items={quotes?.items ?? []} />

      <section className="section">
        <h2>Don&rsquo;t just read the rating</h2>
        <p className="muted section-note">
          Reviews are scored on the things that actually decide whether a product is worth
          trusting with your money.
        </p>
        <div className="grid dims-grid">
          <div>
            <h3 className="dims-head">Exchanges, wallets and protocols</h3>
            <ul className="dims-list">
              {COMPANY_DIMENSIONS.map((d) => <li key={d.key}>{d.label}</li>)}
            </ul>
          </div>
          <div>
            <h3 className="dims-head">Tokens</h3>
            <ul className="dims-list">
              {TOKEN_DIMENSIONS.map((d) => <li key={d.key}>{d.label}</li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* People are right to be sceptical of a crypto review site. Answer it
          directly rather than hoping nobody asks. */}
      <section className="section card independence">
        <h2>Can companies pay for a better rating?</h2>
        <p className="independence-no">No.</p>
        <p>
          A company can claim its profile, correct its details and reply publicly to any review.
          It cannot buy a rating, a Trust Score, a ranking position, or the removal of criticism.
        </p>
        <p>
          This is not a policy we ask you to take on faith. The code that ranks and scores
          listings is never given any billing information, so it cannot favour a paying customer
          — it has no way to know who pays. Automated tests fail our build if anyone connects the
          two.
        </p>
        <p className="independence-line">You can&rsquo;t buy a better rating. You can earn one.</p>
        <Link to="/methodology" className="btn btn-ghost btn-sm">Read the methodology →</Link>
      </section>

      <section className="section card vendor-band">
        <div>
          <h2>Let your users speak for you</h2>
          <p className="muted">
            Claim your profile to correct your details, reply to reviews in public, and show
            people what real customers actually experienced. Claiming and replying are free.
          </p>
        </div>
        <Link to="/dashboard/claim" className="btn btn-primary">Claim your profile</Link>
      </section>
    </div>
  );
}
